import invalidateCanvasConnections from '@/hooks/canvas/helpers/invalidateCanvasConnections';
import buildCanvasQueryKey from '@/hooks/canvas/helpers/buildCanvasQueryKey';
import type { SerializedCanvasConnection } from '@/hooks/canvas/types';
import coerceToRecord from '../helpers/coerceToRecord';
import eventMatchesToolNames from '../helpers/eventMatchesToolNames';
import getStringValue from '../helpers/getStringValue';
import type {
    BuildRefetchCanvasOnConnectionUpdateSubscriptionParams,
    ChatEffectSubscription,
} from '../types';

const CANVAS_CONNECTION_UPDATE_TOOL_NAMES = [
    'update_canvas_connection',
    'canvases.updateConnection',
];

const buildRefetchCanvasOnConnectionUpdateSubscription = (
    _params: BuildRefetchCanvasOnConnectionUpdateSubscriptionParams
): ChatEffectSubscription => {
    return {
        id: 'refetch-canvas-on-connection-update',
        routePattern: '/agents/:agentId/canvas/:canvasId',
        eventType: 'TOOL_CALL_RESULT',
        filter: ({ event, matchParams }) => {
            if (
                !eventMatchesToolNames({
                    event,
                    toolNames: CANVAS_CONNECTION_UPDATE_TOOL_NAMES,
                })
            ) {
                return false;
            }

            const args = coerceToRecord({ value: event.args });
            const canvasIdFromArgs = getStringValue({ value: args.canvasId });

            if (!canvasIdFromArgs) {
                return true;
            }

            return canvasIdFromArgs === matchParams.canvasId;
        },
        action: ({ event, queryClient, matchParams }) => {
            const args = coerceToRecord({ value: event.args });
            const previousConnectionId = getStringValue({ value: args.connectionId });
            const connectionResult = coerceToRecord({ value: event.result });
            const nestedConnectionCandidate = coerceToRecord({
                value: connectionResult.data ?? connectionResult.connection,
            });
            const updatedConnectionRecord =
                getStringValue({ value: nestedConnectionCandidate.connectionId }) !== ''
                    ? nestedConnectionCandidate
                    : connectionResult;
            const updatedConnectionId = getStringValue({
                value: updatedConnectionRecord.connectionId,
            });

            if (previousConnectionId || updatedConnectionId) {
                queryClient.setQueryData(
                    buildCanvasQueryKey({ scope: 'connections', canvasId: matchParams.canvasId }),
                    (existingConnections: SerializedCanvasConnection[] | undefined) => {
                        const safeExistingConnections = existingConnections ?? [];
                        const filteredConnections = safeExistingConnections.filter(
                            existingConnection =>
                                existingConnection.connectionId !== previousConnectionId
                        );

                        if (!updatedConnectionId) {
                            return filteredConnections;
                        }

                        const alreadyExists = filteredConnections.some(
                            existingConnection =>
                                existingConnection.connectionId === updatedConnectionId
                        );
                        if (alreadyExists) {
                            return filteredConnections.map(existingConnection =>
                                existingConnection.connectionId === updatedConnectionId
                                    ? (updatedConnectionRecord as SerializedCanvasConnection)
                                    : existingConnection
                            );
                        }

                        return [
                            ...filteredConnections,
                            updatedConnectionRecord as SerializedCanvasConnection,
                        ];
                    }
                );
            }

            invalidateCanvasConnections({
                queryClient,
                organizationId: matchParams.agentId,
                canvasId: matchParams.canvasId,
            });
        },
    };
};

export default buildRefetchCanvasOnConnectionUpdateSubscription;
